import { useEffect, useState } from "react"
import { topAmenities, amenities } from "shared-components/ViewFragments/SearchResults/Sider/TopBarFilters/AmenitiesFilter/amenities";
import { useUser } from "shared-components/Components/User/User.hook";
import { showFacilities } from "@Utils/Functions";
export interface Amenity {
    id: number | string,
    nombre: string,
    grupo: string,
    IDtipos: number[],
    IDoperaciones: null | number[],
    icon:  React.ComponentType | null;
    countryCodes?: string[];
    logginRequired?: boolean
}
interface AmenitiesGroup {
    amenities: Amenity[],
    group: string
}
interface Group {
    main: Amenity[],
    extra: AmenitiesGroup[]
}

interface AmenitiesHookProps {
    country_code: string,
    openByDefault?: boolean,
    filters?: any,
    filtersTags?: any,
    changeFilters?: (filters: any) => void,
    triggerApplyFilter?: (filters: any) => void,
}

export const useAmenities = ({ country_code, openByDefault = false, filters, filtersTags, changeFilters, triggerApplyFilter } : AmenitiesHookProps) => {
    const {isLoggedIn} = useUser();
    const [showComponent, setShowComponent] = useState(false);
    const [groups, setGroups] = useState<Group>({
        main: [],
        extra: []
    });
    const [showMore, setShowMore] = useState(openByDefault);
    const [mainAmenities, setMainAmenities] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({
        facilitiesGroup: filtersTags?.facilitiesGroup ?? [],
        hasGarage: filtersTags?.hasGarage,
        tour3d: filtersTags?.tour3d,
        privateOwner: filtersTags?.privateOwner,
        socialHousing: filtersTags?.socialHousing,
        seen: filtersTags?.seen,
        seaview: filtersTags?.seaview
    });
  
    useEffect(() => {
      if (country_code) {
        const amenitiesByCountry = topAmenities[country_code];
        const main = typeof amenitiesByCountry !== 'undefined' ? amenitiesByCountry[filters?.operation_type_id] : [];

        setMainAmenities(main);
      }
    }, [country_code])

    useEffect(() => {
      if (filtersTags) {
        setSelectedFilters({
            facilitiesGroup: filtersTags?.facilitiesGroup ?? [],
            hasGarage: filtersTags?.hasGarage,
            tour3d: filtersTags?.tour3d,
            privateOwner: filtersTags?.privateOwner,
            socialHousing: filtersTags?.socialHousing,
            seen: filtersTags?.seen,
            seaview: filtersTags?.seaview
        })
      }
    }, [filtersTags])

    useEffect(() => {
        if ((filters?.property_type_id || filters?.operation_type_id) && !!mainAmenities?.length) {
            const propertyTypes = filters?.property_type_id ?? [];
    
            const allowedAmenities = amenities.filter(
                (amenity) => {
                    if (amenity.countryCodes && !amenity.countryCodes.includes(country_code)) return false;
                    if (amenity.IDoperaciones && !amenity.IDoperaciones.includes(filters?.operation_type_id)) return false;
                    if (amenity.logginRequired && !isLoggedIn) return false;
                    return !propertyTypes.length || amenity.IDtipos === null || propertyTypes.some(item => amenity.IDtipos.includes(item));
                }
            );
    
            const amenitiesGroups = getAmenitiesGroups(allowedAmenities, mainAmenities);
    
            setGroups((prevGroups) => {
                const isSameGroup = JSON.stringify(prevGroups) === JSON.stringify(amenitiesGroups);
                return isSameGroup ? prevGroups : amenitiesGroups;
            });
        }
    
        setShowComponent(showFacilities(
            filters?.property_type_id,
            filters?.operation_type_id
        ) && !!mainAmenities?.length);
    
    }, [filters?.property_type_id, filters?.operation_type_id, mainAmenities]);

    const getAmenitiesGroups = (allowedAmenities, mainAmenities) => {
        const extraAmenitiesGroup = allowedAmenities
            .filter(amenity => !mainAmenities?.includes(amenity.id))
            .reduce((accumulatedAmenities, currentAmenity) => {
                const existingGroup = accumulatedAmenities.find(amenities => amenities.group === currentAmenity.grupo);

                if (existingGroup) {
                    existingGroup.amenities.push(currentAmenity);
                } else {
                    accumulatedAmenities.push({
                        group: currentAmenity.grupo,
                        amenities: [currentAmenity]
                    });
                }
            
                return accumulatedAmenities;
            }, []).sort((a, b) => { //Mover otros al final del array
            if (a.group === "Otros") return 1;
            if (b.group === "Otros") return -1;
            return 0;
        })
    
        const mainAmenitiesGroup = mainAmenities.map((amenity) => {
            const item = allowedAmenities.find((allowed) => allowed.id === amenity)
            if (item) return item
        }).filter((amenity) => amenity);

        return { main: mainAmenitiesGroup, extra: extraAmenitiesGroup }
    }

    const handleChange = (filter) => {
        setSelectedFilters((prev) => ({...prev, ...filter}));

        if (triggerApplyFilter) triggerApplyFilter(filter)
        else changeFilters(filter)
    }

    const handleSelect = (option) => {
        const optionId = option.id;
        const { hasGarage, tour3d, privateOwner, socialHousing, seen, seaview } = filters;
        const amenitiesJustValues = selectedFilters.facilitiesGroup.map(amenity => amenity.value)

        if(optionId === 'garage') {
            if(hasGarage) {
              handleChange({hasGarage: null})
            } else {
              handleChange({hasGarage: {value: true, text: 'Garaje'}})
            }
            return;
        }

        if(optionId === 'virtualTour') {
            if(tour3d) {
                handleChange({tour3d: null})
            } else {
                handleChange({tour3d: {value: true, text: 'Tour virtual'}})
            }
            return;
        }

        if(optionId === 'privateOwner') {
            if(privateOwner) {
                handleChange({privateOwner: null})
            } else {
                handleChange({privateOwner: {value: true, text: 'Dueño'}})
            }
            return;
        }

        if(optionId === 'socialHousing') {
            if(socialHousing) {
                handleChange({socialHousing: null})
            } else {
                handleChange({socialHousing: {value: true, text: 'Vivienda social'}})
            }
            return;
        }

        if(optionId === 'hideSeen') {
            if(seen) {
                handleChange({seen: null})
            } else {
                handleChange({seen: {value: true, text: 'Ocultar visitadas'}})
            }
            return;
        }
    
        if(optionId === 'seaView') {
            if(seaview) {
                handleChange({seaview: null})
            } else {
                handleChange({seaview: {value: true, text: 'Vista al mar'}})
            }
            return;
        }

        if(selectedFilters.facilitiesGroup && amenitiesJustValues.includes(option.id)) {
            const optionsFiltered = selectedFilters.facilitiesGroup.filter(selected => !(selected.value === option.id));

            handleChange({facilitiesGroup: optionsFiltered});
        } else {
            const newValue = [
                ...selectedFilters.facilitiesGroup,
                {value: option.id, text: option.nombre}
            ];

            handleChange({facilitiesGroup: newValue});
        }
    }

    const isActive = (amenityId) => {
        const amenitiesJustValues = selectedFilters.facilitiesGroup.map(amenity => amenity.value)

        if (amenityId === "garage" && selectedFilters?.hasGarage) return true
        else if (amenityId === "virtualTour" && selectedFilters?.tour3d) return true
        else if (amenityId === "privateOwner" && selectedFilters?.privateOwner) return true
        else if (amenityId === "socialHousing" && selectedFilters?.socialHousing) return true
        else if (amenityId === "hideSeen" && selectedFilters?.seen) return true
        else if (amenityId === "seaView" && selectedFilters?.seaview) return true
        else if (selectedFilters?.facilitiesGroup && amenitiesJustValues.includes(amenityId)) return true
        else return false
    }

    const ammenitieActiveIsNotMain = filters?.facilitiesGroup && country_code !== "CO" ? filters?.facilitiesGroup.some(amenitie => !mainAmenities.includes(amenitie)) : false

    return {
        showComponent,
        amenitiesToShow: groups,
        showMore: {
            value: showMore,
            set: setShowMore
        },
        handleSelect,
        isActive,
        ammenitieActiveIsNotMain
    }
}